import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";

import { toggleEditMode, updateWidgetProperty, toggleHotspotView, unselectWidget } from 'actions/editor';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';

import { EditorContext } from '../../index';
import { Property } from '../../components/properties/';
import { Container } from '../core/container';
import Portal from 'components/Portal';
import { useChildrenWidgetSelected, useWidgetSelector, useWidgetSelected } from 'components/Editor/hooks/useEditorSession';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';
import { Background } from 'components/Editor/components/properties/background';
import { getBoundingRectFromLayout, getWidgetPositionForBreakpoint } from 'components/Editor/helper/layoutManager';
import { BackgroundView } from '../background';
import { getBoxShadow } from 'services/cssHelper.service';

import styles from './hotspot.module.scss';
import { getMediaInfo } from 'actions/pageHealth';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const HotspotWidgetComponent = function ({ widgetProps, widgetRef, className, style, children }) {
  const ref = widgetRef;

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const properties = _.get(widgetProps, 'properties', {});

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const selected = useWidgetSelected(editor, id);
  const childrenSelection = useChildrenWidgetSelected(editor, id);
  const parent = useWidgetSelector(editor, widgetProps.parent);

  const background = _.get(properties, 'desktop.background', {});

  const HotspotParentIndicator = () => {
    const parentElement = document.getElementById(parent.uid);
    return parentElement ? (
      <Portal mountNode={parentElement}>
        <div className={styles.hotspotParentIndicator}></div>
      </Portal>
    ) : null;
  }

  var widgetLayoutStyle = {
    ...style,
    ...getLayoutStyles(editor, parent, breakpoint),
  }

  var widgetStyles = {
    ...getStyles(widgetProps, breakpoint),
    width: '100%',
    height: style.height,
    borderStyle: 'solid',
    position: 'relative'
  }

  var backgroundStyle = getBackgroundStyle(properties, breakpoint);

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div id={id}
        ref={ref}
        className={cx(className, styles.hotspotWidget, 'cs_hotspot_widget', {
          [styles.mobile]: breakpoint === 'mobile',
          [styles.partialSelection]: childrenSelection.selected
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-container={true}
        data-selectable={true}
        style={widgetLayoutStyle}
      >
        <Animation widget={widgetProps}>
          <div style={widgetStyles}>
            <BackgroundView background={background} style={backgroundStyle} />
            {!(selected || childrenSelection.selected) &&
              <div className={styles.overlay} onClick={(e) => {
                e.stopPropagation();
              }}></div>
            }
            {children}
          </div>
        </Animation>
      </div>
      {selected && parent && <HotspotParentIndicator />}
    </Container>
  )
}

const hotspotWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef', 'children'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const HotspotWidget = React.memo(HotspotWidgetComponent, hotspotWidgetEquals);

const HotspotWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });
  const entityType = useSelector(state => _.get(state, `${editorSession}.present.entityType`));
  const background = _.get(props, 'properties.desktop.background', {});

  useEffect(() => {
    if (breakpoint === 'mobile') {
      dispatch(updateWidgetProperty(editorSession, id, breakpoint, {
        trigger: 'click'
      }));
    }
  }, [breakpoint, editorSession, id]);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const handleClose = () => {
    dispatch(toggleHotspotView(editorSession, null));
    dispatch(unselectWidget(editorSession, id))
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Border' onClick={() => changeView('borders')}>
        <i className={cx("cleverstory-corner", styles.gridIcon)} />
      </Property.Action>
      {breakpoint === 'desktop' &&
      <Property.Action className={styles.iconButton} tooltip='Toggle' onClick={() => changeView('settings')}>
        <i className={cx("cleverstory-settings", styles.settingsIcon)} />
      </Property.Action>
      }
      {entityType === 'PAGE' &&
        <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
          <i className={cx("cleverstory-animations")} />
        </Property.Action>
      }
      <Property.Action onClick={handleClose} className={styles.iconButton} tooltip='Close'>
        <i className={cx(styles.closeButton, 'cleverstory-close')} />
      </Property.Action>

      {!editMode && view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          showShadow={true}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            changeView(null)
          }}
        />
      }

      {view === 'borders' &&
        <BorderPropertyDialog
          id={id}
          editorSession={editorSession}
          widget={props}
          onCancel={() => {
            changeView(null);
          }}
        />
      }

      {view === 'settings' &&
        <SettingsPropertyDialog
          id={id}
          editorSession={editorSession}
          widget={props}
          onCancel={() => {
            changeView(null)
          }} />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          exclusions={['parallax', 'spin']}
          onCancel={() => {
            changeView(null)
          }}
        />
      }
    </>
  )
}

HotspotWidget.actions = (editor, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return (
      <HotspotWidgetDesktopAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  if (breakpoint === 'mobile') {
    return (
      <HotspotWidgetDesktopAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  return false;
}

const SettingsPropertyDialog = ({ id, editorSession, widget, ...rest }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const trigger = _.get(widget, `properties.${breakpoint}.trigger`) || 'hover';

  const toggleTrigger = (trigger) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, {
      trigger: trigger
    }));
  }

  return (
    <>
      <Property.Dialog className={styles.settingsDialog}
        width={246}
        target={`#${id}`}
        title={'Hotspot Toggle'}
        onCancel={rest.onCancel}
      >
        <Property name="Pop-up On and Off">
          <div className={styles.settingsContainer}>
            <Property.RadioButton className={styles.targetRadio}
              title='On hover'
              value={trigger === 'hover'}
              onClick={() => toggleTrigger('hover')}
            />
            <Property.RadioButton className={styles.targetRadio}
              title='On Click'
              value={trigger === 'click'}
              onClick={() => toggleTrigger('click')}
            />
          </div>
        </Property>
      </Property.Dialog>
    </>
  );
}

const BorderPropertyDialog = ({ id, editorSession, widget, ...rest }) => {
  const dispatch = useDispatch();

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const properties = _.get(widget, `properties.${breakpoint}`, {});

  const border = _.get(properties, 'border', {});
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handleBorderColorChange = (color) => {
    var request = {
      ...properties,
      border: {
        ...border,
        color: color
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderWidthChange = (value) => {
    var request = {
      ...properties,
      border: {
        ...border,
        width: value
      }
    }

    handlePropertyChange(request);
  }

  const handleBorderRadiusChange = (radius) => {
    var request = {
      ...properties,
      border: {
        ...border,
        radius: radius
      }
    }

    handlePropertyChange(request);
  }

  return (
    <>
      <Property.Dialog className={styles.fontDialog}
        width={246}
        target={`#${id}`}
        title={'Border'}
        onCancel={rest.onCancel}
      >
        <Property.Color title={'Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />
        <Property name="Stroke Width" className={styles.buttonStyleProperty}>
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.formStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>
      </Property.Dialog>
    </>
  );
};

function getBackgroundStyle(properties, breakpoint) {
  var styles = {
    overflow: 'hidden'
  };

  const defaultProperties = _.get(properties, `${breakpoint}`);

  const border = _.get(defaultProperties, 'border');

  const borderWidthString = _.get(border, 'width') || 0;
  var borderWidget = parseInt(borderWidthString) + 'px';

  var topLeft = _.get(border, 'radius.topLeft', null);
  var topRight = _.get(border, 'radius.topRight', null);
  var bottomLeft = _.get(border, 'radius.bottomLeft', null);
  var bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = `calc(${parseInt(topLeft) + 'px'} - ${borderWidget})`;
  }

  if (topRight) {
    styles['borderTopRightRadius'] = `calc(${parseInt(topRight) + 'px'} - ${borderWidget})`;
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = `calc(${parseInt(bottomLeft) + 'px'} - ${borderWidget})`;
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = `calc(${parseInt(bottomRight) + 'px'} - ${borderWidget})`;
  }

  return styles;
}

HotspotWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

HotspotWidget.moveableConfig = (editorSession, widget) => {
  return {
    draggable: true,
    resizable: true,
    rotatable: false,
    selectable: true,
    cloneable: true,
    deletable: true,
    renderDirections: ['e', 'w', 'n', 's'],
    onReparent: (widget, parent) => {
      return false
    }
  }
}

HotspotWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background
      }
    }
  }
}

/**
 * Generate Styles
 * 
*/
function getStyles(widget, breakpoint) {
  if (!widget) {
    return null;
  }

  const border = _.get(widget, `properties.${breakpoint}.border`);

  var styles = {};
  const topLeft = _.get(border, 'radius.topLeft', null);
  const topRight = _.get(border, 'radius.topRight', null);
  const bottomLeft = _.get(border, 'radius.bottomLeft', null);
  const bottomRight = _.get(border, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(border, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, ${colorRgb.a})`;
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(border, 'width', 0);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  //DropShadow
  const shadow = _.get(widget, `properties.${breakpoint}.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;
}

function getLayoutStyles(editorSession, parent, breakpoint) {
  var styles = {};

  var parentRect = getWidgetPositionForBreakpoint(editorSession, parent.uid, breakpoint);
  if(parentRect){
    styles['marginLeft'] = `${parentRect.x}px`;
    styles['marginTop'] = `${parentRect.y}px`;
  }

  return styles;
}

export { HotspotWidget };